import type { ActionTree } from "vuex";
import {
  LOAD_ALERTS,
  LOAD_ALERTS_SUCCESS,
  LOAD_ALERTS_FAILURE,
  LOAD_ALERTS_EXPORT,
  LOAD_ALERTS_EXPORT_SUCCESS,
  LOAD_ALERTS_EXPORT_FAILURE,
  LOAD_TYPES,
  LOAD_TYPES_SUCCESS,
  LOAD_TYPES_FAILURE,
  LOAD_LEVELS,
  LOAD_LEVELS_SUCCESS,
  LOAD_LEVELS_FAILURE,
  LOAD_SCENARIOS,
  LOAD_SCENARIOS_SUCCESS,
  LOAD_SCENARIOS_FAILURE,
  ADD_SCENARIO,
  ADD_SCENARIO_SUCCESS,
  ADD_SCENARIO_FAILURE,
  DELETE_SCENARIO,
  DELETE_SCENARIO_SUCCESS,
  DELETE_SCENARIO_FAILURE,
  UPDATE_SCENARIO,
  UPDATE_SCENARIO_SUCCESS,
  UPDATE_SCENARIO_FAILURE,
  ADD_ALERT,
  ADD_ALERT_SUCCESS,
  ADD_ALERT_FAILURE,
  DELETE_ALERT,
  DELETE_ALERT_SUCCESS,
  DELETE_ALERT_FAILURE,
  UPDATE_ALERT,
  UPDATE_ALERT_SUCCESS,
  UPDATE_ALERT_FAILURE,
  LOAD_EVENTS,
  LOAD_EVENTS_SUCCESS,
  LOAD_EVENTS_FAILURE,
  LOAD_EVENTS_EXPORT,
  LOAD_EVENTS_EXPORT_SUCCESS,
  LOAD_EVENTS_EXPORT_FAILURE,
  LOAD_ONE_EVENT,
  LOAD_ONE_EVENT_SUCCESS,
  LOAD_ONE_EVENT_FAILURE,
  LOAD_ONE_EVENT_VAR,
  LOAD_ONE_EVENT_VAR_SUCCESS,
  LOAD_ONE_EVENT_VAR_FAILURE,
  LOAD_COMMENTS,
  LOAD_COMMENTS_SUCCESS,
  LOAD_COMMENTS_FAILURE,
  ADD_COMMENT,
  ADD_COMMENT_SUCCESS,
  ADD_COMMENT_FAILURE,
  UPDATE_COMMENT,
  UPDATE_COMMENT_SUCCESS,
  UPDATE_COMMENT_FAILURE,
  DELETE_COMMENT,
  DELETE_COMMENT_SUCCESS,
  DELETE_COMMENT_FAILURE,
  CLEAR_ALERTS_STORE,
  CLEAR_EVENTS_ALERT_STORE,
  LOAD_ALERT_SCENARIOS,
  LOAD_ALERT_SCENARIOS_SUCCESS,
  LOAD_ALERT_SCENARIOS_FAILURE,
  LOAD_VARIABLE_DATA,
  LOAD_VARIABLE_DATA_SUCCESS,
  LOAD_VARIABLE_DATA_FAILURE,
  INIT_DIAGNOSTIC_DATA,
  ADD_DIAGNOSTIC_DATA_SUCCESS,
  ADD_DIAGNOSTIC_DATA_FAILURE,
  ADD_DIAGNOSTIC_DATA,
  LOAD_PROJECT_ALERTS,
  LOAD_PROJECT_ALERTS_SUCCESS,
  UPDATE_PROJECT_ALERT_SUCCESS,
  UPDATE_PROJECT_ALERT,
  LOAD_PROJECT_ALERTS_FAILURE,
  UPDATE_PROJECT_ALERT_FAILURE,
  LOAD_ONE_EVENT_PROJECT_VAR,
  LOAD_ONE_EVENT_PROJECT_VAR_SUCCESS,
  LOAD_ONE_EVENT_PROJECT_VAR_FAILURE,
  LOAD_ONE_EVENT_VAR_FAILURE_IMPORTANT,
  LOAD_ONE_EVENT_VAR_SUCCESS_IMPORTANT,
  LOAD_ONE_EVENT_VAR_IMPORTANT,
  LOAD_MANY_EVENT_VAR_SUCCESS,
} from "./mutations";
import { httpClient, type AxiosResponse } from "@/services/httpClient";
import type {
  RootState,
  AlertingState,
  Alert,
  AlertFunction,
  AlertLevel,
  Scenario,
  GraphData,
  AlertEventComment,
} from "@/types";
import { HOSTS, ENDPOINTS } from "@/constants";
import { dateFormatterGenerator, eventPeriode } from "@/common/date";
import { exportEventsTableCsv, exportAlertsTableCsv } from "@/common/csv";
import { translateStatusFromEnglishToFrench } from "@/common/utils";
import router from "@/router";
import { dateFormatterEnd, dateFormatterStart } from "@/common/events";
import { prepareVariableParameters } from "@/controllers/alertingController.ts";

export const actions: ActionTree<AlertingState, RootState> = {
  // LOAD ALERTS
  fetchAlerts({ commit }, params: any) {
    commit(LOAD_ALERTS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.ALERT}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<Alert[]>) => {
          commit(LOAD_ALERTS_SUCCESS, response);
        },
        (error) => {
          commit(LOAD_ALERTS_FAILURE, error.message);
        },
      );
  },
  fetchAlertsForExport({ commit }, params: any) {
    const { alarmListColumns, ...requestParams }: any = params;
    commit(LOAD_ALERTS_EXPORT);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.ALERT}`, {
        params: requestParams,
      })
      .then(
        (response: AxiosResponse<Alert[]>) => {
          commit(LOAD_ALERTS_EXPORT_SUCCESS);
          exportAlertsTableCsv(
            response.data.map((alert: any) => ({
              Client: alert.project.place.client.name,
              Site: alert.project.place.name,
              Projet: alert.project.name,
              Alert: alert.name,
              Variable: alert.alertVariables
                ?.map((alertVariable: any) => alertVariable.variable.name)
                .join("/"),
              IdTechnique: alert.alertVariables
                ?.map(
                  (alertVariable: any) =>
                    alertVariable.variable.sourceTechnicalName,
                )
                .join("/"),
              Type: alert.activationFunction?.name,
              Criticite: alert.alertLevel?.name,
              Scenario: alert.scenarios
                .map((scenario: any) => scenario.name)
                .join("/"),
              Seuil: alert.activationParameters?.threshold,
              NotifiedContacts: alert.contacts
                ?.map((contact: any) => contact.name)
                .join("/"),
              EmailContacts: alert.contacts
                ?.map((contact: any) => contact.email)
                .join("/"),
              Activated: alert.enabled ? "Pas en sourdine" : "En sourdine",
            })),
            `alerts_list_${dateFormatterGenerator(new Date().toString())}`,
            alarmListColumns,
          );
        },
        (error) => {
          commit(LOAD_ALERTS_EXPORT_FAILURE, error.message);
        },
      );
  },
  // LOAD ALERTS
  fetchProjectAlertsForExport({ commit }, params: any) {
    const { alarmListColumns, ...requestParams }: any = params;
    commit(LOAD_ALERTS_EXPORT);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_ALERT}`, {
        params: {
          ...requestParams,
          variableName: undefined,
          scenarioName: undefined,
          variableSourceTechnicalName: undefined,
        },
      })
      .then(
        (response: any) => {
          commit(LOAD_ALERTS_EXPORT_SUCCESS);
          exportAlertsTableCsv(
            response.data.map((alert: any) => ({
              Client: alert.project.place.client.name,
              Site: alert.project.place.name,
              Projet: alert.project.name,
              Alert: alert.name,
              Type: alert.function?.name,
              Criticite: alert.alertLevel?.name,
              Seuil: alert.activationParameters?.threshold,
              NotifiedContacts: alert.contacts
                ?.map((contact: any) => contact.name)
                .join("/"),
              EmailContacts: alert.contacts
                ?.map((contact: any) => contact.email)
                .join("/"),
              Activated: alert.enabled ? "Pas en sourdine" : "En sourdine",
            })),
            `alerts_list_${dateFormatterGenerator(new Date().toString())}`,
            alarmListColumns,
          );
        },
        (error) => {
          commit(LOAD_ALERTS_EXPORT_FAILURE, error.message);
        },
      );
  },
  fetchAlertById({ commit }, alertId: string) {
    commit(LOAD_ALERTS);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.ALERT}/${alertId}`).then(
      (response: AxiosResponse<any>) => {
        commit(UPDATE_ALERT_SUCCESS, {
          ...response.data,
          alertVariables: [
            {
              variable: response.data.alertVariables[0]?.variable,
              unit: response.data.alertVariables[0]?.variable.unit,
              aggregation: response.data.alertVariables[0]?.aggregation,
              sampling: response.data.alertVariables[0]?.sampling,
              samplingUnit: response.data.alertVariables[0]?.samplingUnit,
            },
          ],
          scenarios: response.data.scenarios.map((sc: any) => ({
            id: sc.id,
            name: sc.name,
          })),
        });
      },
      (error) => {
        commit(UPDATE_ALERT_FAILURE, error.message);
      },
    );
  },

  // LOAD ALERTS
  fetchProjectAlerts({ commit }, params: any) {
    commit(LOAD_PROJECT_ALERTS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_ALERT}`, {
        params: { ...params, variableName: undefined, scenarioName: undefined },
      })
      .then(
        (response: any) => {
          commit(LOAD_PROJECT_ALERTS_SUCCESS, {
            alertsProject: response.data.map((alert: any) => {
              return {
                ...alert,
                projects: [alert.project],
                activationFunction: alert.function,
              };
            }),
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_PROJECT_ALERTS_FAILURE, error.message);
        },
      );
  },

  fetchProjectAlertById({ commit }, alertId: string) {
    commit(LOAD_PROJECT_ALERTS);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_ALERT}/${alertId}`).then(
      (response: any) => {
        commit(UPDATE_PROJECT_ALERT_SUCCESS, response.data);
      },
      (error) => {
        commit(UPDATE_PROJECT_ALERT_FAILURE, error.message);
      },
    );
  },

  // ADD ALERTS
  addAlert({ commit }, alert: Alert) {
    commit(ADD_ALERT);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.ALERT}`, {
        ...alert,
        id: undefined,
      })
      .then(
        (response: AxiosResponse<any>) => {
          commit(ADD_ALERT_SUCCESS, {
            ...response.data,
            alertVariables: [
              {
                variable: response.data.alertVariables[0].variable,
                unit: response.data.alertVariables[0].variable.unit,
                aggregation: response.data.alertVariables[0].aggregation,
                sampling: response.data.alertVariables[0].sampling,
                samplingUnit: response.data.alertVariables[0].samplingUnit,
              },
            ],
            scenarios: response.data.scenarios.map((sc: any) => ({
              id: sc.id,
              name: sc.name,
            })),
          });
        },
        (error) => {
          commit(ADD_ALERT_FAILURE, error.message);
        },
      );
  },

  // ADD ALERTS
  updateAlert({ commit }, alert: Alert) {
    commit(UPDATE_ALERT);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.ALERT}/${alert.id}`, {
        ...alert,
        id: undefined,
        path: undefined,
      })
      .then(
        (response: AxiosResponse<any>) => {
          commit(UPDATE_ALERT_SUCCESS, {
            ...response.data,
            alertVariables: [
              {
                variable: response.data.alertVariables[0].variable,
                unit: response.data.alertVariables[0].variable.unit,
                aggregation: response.data.alertVariables[0].aggregation,
                sampling: response.data.alertVariables[0].sampling,
                samplingUnit: response.data.alertVariables[0].samplingUnit,
              },
            ],
            scenarios: response.data.scenarios.map((sc: any) => ({
              id: sc.id,
              name: sc.name,
            })),
          });
          if (alert.path) {
            router.push({
              path: `${alert.path}`,
            });
          }
        },
        (error) => {
          commit(UPDATE_ALERT_FAILURE, error.message);
        },
      );
  },
  updateProjectAlert({ commit }, alert: Alert) {
    commit(UPDATE_PROJECT_ALERT);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_ALERT}/${alert.id}`, {
        ...alert,
        id: undefined,
        path: undefined,
      })
      .then(
        (response: AxiosResponse<Alert>) => {
          commit(UPDATE_PROJECT_ALERT_SUCCESS, response.data);
          if (alert.path) {
            router.push({
              path: `${alert.path}`,
            });
          }
        },
        (error) => {
          commit(UPDATE_PROJECT_ALERT_FAILURE, error.message);
        },
      );
  },
  updateAlertToActive({ commit }, alert: Alert) {
    commit(UPDATE_ALERT);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.ALERT}/${alert.id}`, {
        ...alert,
        id: undefined,
        path: undefined,
      })
      .then(
        () => {
          commit(DELETE_ALERT_SUCCESS, alert);
        },
        (error) => {
          commit(UPDATE_ALERT_FAILURE, error.message);
        },
      );
  },

  // DELETE_ALERT
  deleteAlert({ commit }, alert: Alert) {
    commit(DELETE_ALERT);
    httpClient.delete(`${HOSTS.AWS}/${ENDPOINTS.ALERT}/${alert.id}`).then(
      () => {
        commit(DELETE_ALERT_SUCCESS, alert);
      },
      (error) => {
        commit(DELETE_ALERT_FAILURE, error.message);
      },
    );
  },

  // TYPE_ALERT
  fetchTypesAlerts({ commit }) {
    commit(LOAD_TYPES);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.TYPE_ALERT}`).then(
      (response: AxiosResponse<AlertFunction[]>) => {
        commit(LOAD_TYPES_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_TYPES_FAILURE, error.message);
      },
    );
  },

  // LEVEl_ALERT
  fetchLevelsAlerts({ commit }) {
    commit(LOAD_LEVELS);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.LEVEL_ALERT}`).then(
      (response: AxiosResponse<AlertLevel[]>) => {
        commit(LOAD_LEVELS_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_LEVELS_FAILURE, error.message);
      },
    );
  },

  // LOAD_EVENTS
  fetchAlertEvents({ commit }, params: any) {
    commit(LOAD_EVENTS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.EVENTS}`, {
        params: params,
      })
      .then(
        (response: any) => {
          commit(LOAD_EVENTS_SUCCESS, {
            events: response.data,
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_EVENTS_FAILURE, error.message);
        },
      );
  },
  fetchAlertEventsForExport({ commit }, params: any) {
    const { eventListColumns, ...requestParams }: any = params;
    commit(LOAD_EVENTS_EXPORT);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.EVENTS}`, {
        params: { ...requestParams },
      })
      .then(
        (response: any) => {
          commit(LOAD_EVENTS_EXPORT_SUCCESS);
          exportEventsTableCsv(
            response.data.map((event: any) => ({
              Client: event.project.place.client.name,
              Site: event.project.place.name,
              Project: event.project.name,
              Alert: event.alert.name,
              Variable: event.alert.alertVariables
                ?.map((alertVariable: any) => alertVariable.variable.name)
                .join("/"),
              IdTechnique: event.alert.alertVariables
                ?.map(
                  (alertVariable: any) =>
                    alertVariable.variable.sourceTechnicalName,
                )
                .join("/"),
              Criticite: event.alert.alertLevel?.name,
              Debut: dateFormatterStart(event),
              Fin: dateFormatterEnd(event),
              Status: translateStatusFromEnglishToFrench(event.status),
            })),
            `events_list_${dateFormatterGenerator(new Date().toString())}`,
            eventListColumns,
          );
        },
        (error) => {
          commit(LOAD_EVENTS_EXPORT_FAILURE, error.message);
        },
      );
  },

  // LOAD_ALERT_SCENARIOS
  fetchAlertscs({ commit }, params: any) {
    commit(LOAD_ALERT_SCENARIOS);
    Promise.all(
      params.scenarios.map((sc: any) => {
        return httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.SC_TIME}`, {
          params: {
            ...params,
            scenarios: undefined,
            scenarioId: sc.id,
            limit: 10000,
            timeserie: true,
          },
        });
      }),
    ).then(
      (res: any) => {
        const response = [
          ...res.map((datsc: any) => {
            return [...datsc.data.data];
          }),
        ];

        let data: number[] = [];
        response.forEach((arr: number[]) => {
          if (data.length === 0) {
            data = [...arr];
          } else {
            for (const i in arr) {
              data[i] = data[i] || arr[i];
            }
          }
        });

        commit(LOAD_ALERT_SCENARIOS_SUCCESS, data);
      },
      (error) => {
        commit(LOAD_ALERT_SCENARIOS_FAILURE, error.message);
      },
    );
  },

  updateAlertEventstatus({ commit }, event: any) {
    commit(LOAD_ONE_EVENT);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${event.id}`, {
        cancelled: event.cancelled,
        tags: event.tags,
        status: event.status,
      })
      .then(
        () => {
          commit(LOAD_ONE_EVENT_SUCCESS, { ...event, updateStatus: true });
        },
        (error) => {
          commit(LOAD_ONE_EVENT_FAILURE, error.message);
        },
      );
  },
  // LOAD_EVENTS
  fetchOneAlertEvents({ commit, dispatch }, params: any) {
    commit(LOAD_ONE_EVENT);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${params.eventId}`).then(
      (response: AxiosResponse<any>) => {
        const isCom = !!response.data.alert.function;
        commit(LOAD_ONE_EVENT_SUCCESS, {
          ...response.data,
          includedVars: isCom
            ? response.data.alert.variables.length
            : undefined,
        });
        if (isCom) {
          const periode = eventPeriode([
            response.data.dateStart,
            response.data.dateEnd,
          ]);
          dispatch(
            "alerting/fetchEventComData",
            {
              start: periode[0],
              end: periode[1],
              limit: 10000,
              id: response.data.alert.id,
            },
            {
              root: true,
            },
          );
          dispatch(
            "alerting/fetchEventComDataImportant",
            {
              start: periode[0],
              end: periode[1],
              limit: 10000,
              id: response.data.alert.id,
              related: true,
            },
            {
              root: true,
            },
          );
        } else {
          const avs = response.data.alert.alertVariables;
          if (avs.length > 0) {
            const variableParameters = prepareVariableParameters(
              response.data.alert,
              response.data.dateStart,
              response.data.dateEnd,
              avs,
            );
            dispatch("alerting/syncFetchManyEventData", variableParameters, {
              root: true,
            });
          }
        }
      },
      (error) => {
        commit(LOAD_ONE_EVENT_FAILURE, error.message);
      },
    );
  },

  fetchEventData({ commit }, params: any) {
    commit(LOAD_ONE_EVENT_VAR);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`, {
        params: {
          id: params.variable.id,
          aggregationId: params.variable.aggregationId
            ? params.variable.aggregationId
            : params.variable.aggregation.id,
          type: "variable",
          start: params.period ? params.period[0] : "",
          end: params.period ? params.period[1] : "",
          preferredSampling: params.variable.sampling,
          preferredSamplingUnit: params.variable.samplingUnit,
          timeserie: true,
          limit: 10000,
        },
      })
      .then(
        (response: AxiosResponse<GraphData>) => {
          commit(LOAD_ONE_EVENT_VAR_SUCCESS, {
            ...params.variable,
            aggregationId: params.variable.aggregationId
              ? params.variable.aggregationId
              : params.variable.aggregation.id,
            aggregation: params.variable.aggregation.name
              ? params.variable.aggregation.name
              : params.variable.aggregation,
            data: response.data.data, //noNullInArray(response.data.data),
            unit: response.data.unit.name,
            sampling: response.data.sampling,
            samplingUnit: response.data.samplingUnit,
          });
        },
        (error) => {
          commit(LOAD_ONE_EVENT_VAR_FAILURE, error.message);
        },
      );
  },

  syncFetchManyEventData({ commit }, paramsList: any[]) {
    commit(LOAD_ONE_EVENT_VAR);
    const promises = paramsList.map((params: any) => {
      const variable = params.variable;
      const period = params.period;
      const start = period ? period[0] : "";
      const end = period ? period[1] : "";
      const aggregationId = variable.aggregationId || variable.aggregation.id;

      const queryParameters = {
        params: {
          id: variable.id,
          aggregationId,
          type: "variable",
          start,
          end,
          preferredSampling: variable.sampling,
          preferredSamplingUnit: variable.samplingUnit,
          timeserie: true,
          limit: 10000,
        },
      };
      return httpClient.get(
        `${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`,
        queryParameters,
      );
    });

    const variableParamsMap = {};
    for (const params of paramsList) {
      variableParamsMap[params.variable.id] = params;
    }

    Promise.all(promises).then(
      (responses: any[]) => {
        // get all variable values synchronized at once
        const variableDataList: any[] = [];

        for (const response of responses) {
          const variable = variableParamsMap[response.data.id].variable;
          const aggregationId =
            variable.aggregationId || variable.aggregation.id;
          const aggregation = variable.aggregation.name || variable.aggregation;
          const data = response.data;

          variableDataList.push({
            ...variable,
            aggregationId,
            aggregation,
            data: data.data, //noNullInArray(response.data.data),
            unit: data.unit.name,
            sampling: data.sampling,
            samplingUnit: data.samplingUnit,
          });
        }
        commit(LOAD_MANY_EVENT_VAR_SUCCESS, variableDataList);
      },
      (error) => {
        commit(LOAD_ONE_EVENT_VAR_FAILURE, error.message);
      },
    );
  },
  fetchEventComData({ commit }, params: any) {
    commit(LOAD_ONE_EVENT_VAR);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_DATA}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<any>) => {
          commit(LOAD_ONE_EVENT_VAR_SUCCESS, {
            ...response.data,
          });
        },
        (error) => {
          commit(LOAD_ONE_EVENT_VAR_FAILURE, error.message);
        },
      );
  },

  fetchEventComDataImportant({ commit }, params: any) {
    commit(LOAD_ONE_EVENT_VAR_IMPORTANT);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_DATA}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<any>) => {
          commit(LOAD_ONE_EVENT_VAR_SUCCESS_IMPORTANT, {
            ...response.data,
          });
        },
        (error) => {
          commit(LOAD_ONE_EVENT_VAR_FAILURE_IMPORTANT, error.message);
        },
      );
  },

  fetchProjectVariablesDetails({ commit }, params: any) {
    commit(LOAD_ONE_EVENT_PROJECT_VAR);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.PROJECT_DATA}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<any>) => {
          commit(LOAD_ONE_EVENT_PROJECT_VAR_SUCCESS, [
            ...response.data.data[0].value.variablesAlerting.map(
              (variable: any) => {
                const baseVariable = response.data.variables.find(
                  (baseVar: any) => baseVar.id === variable.id,
                );
                return baseVariable && baseVariable.id
                  ? { ...baseVariable, value: variable.value }
                  : variable;
              },
            ),
          ]);
        },
        (error) => {
          commit(LOAD_ONE_EVENT_PROJECT_VAR_FAILURE, error.message);
        },
      );
  },

  // SCENARIOS
  fetchScenarios({ commit }, placeId?: string) {
    commit(LOAD_SCENARIOS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}`, {
        params: { placeId },
      })
      .then(
        (response: AxiosResponse<Scenario[]>) => {
          commit(LOAD_SCENARIOS_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_SCENARIOS_FAILURE, error.message);
        },
      );
  },

  addScenario({ commit }, scenario: Scenario) {
    commit(ADD_SCENARIO);
    httpClient.post(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}`, scenario).then(
      (response: AxiosResponse<Scenario>) => {
        commit(ADD_SCENARIO_SUCCESS, response.data);
      },
      (error) => {
        commit(ADD_SCENARIO_FAILURE, error.message);
      },
    );
  },

  updateScenario({ commit }, scenario: Scenario) {
    commit(UPDATE_SCENARIO);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}/${scenario?.id}`, {
        ...scenario,
        deletable: undefined,
        updated: undefined,
        id: undefined,
      })
      .then(
        (response: AxiosResponse<Scenario>) => {
          commit(UPDATE_SCENARIO_SUCCESS, response.data);
        },
        (error) => {
          commit(UPDATE_SCENARIO_FAILURE, error.message);
        },
      );
  },

  deleteScenario({ commit }, scenario: Scenario) {
    commit(DELETE_SCENARIO);
    httpClient
      .delete(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}/${scenario?.id}`)
      .then(
        () => {
          commit(DELETE_SCENARIO_SUCCESS, scenario);
        },
        (error) => {
          commit(DELETE_SCENARIO_FAILURE, error.message);
        },
      );
  },

  // COMMENTS
  fetchComments({ commit }, alertEventId: string) {
    commit(LOAD_COMMENTS);
    httpClient
      .get(
        `${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${alertEventId}/${ENDPOINTS.COMMENTS}`,
      )
      .then(
        (response: AxiosResponse<AlertEventComment[]>) => {
          commit(LOAD_COMMENTS_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_COMMENTS_FAILURE, error.message);
        },
      );
  },

  addComment({ commit }, params: any) {
    commit(ADD_COMMENT);
    httpClient
      .post(
        `${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${params?.alertEventId}/${ENDPOINTS.COMMENTS}`,
        {
          comment: params?.comment,
        },
      )
      .then(
        (response: AxiosResponse<AlertEventComment>) => {
          commit(ADD_COMMENT_SUCCESS, response.data);
        },
        (error) => {
          commit(ADD_COMMENT_FAILURE, error.message);
        },
      );
  },

  updateComment({ commit }, comment: AlertEventComment) {
    commit(UPDATE_COMMENT);
    httpClient
      .put(
        `${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${comment?.alertEvent.id}/${ENDPOINTS.COMMENTS}/${comment?.id}`,
        {
          comment: comment?.comment,
        },
      )
      .then(
        (response: AxiosResponse<AlertEventComment>) => {
          commit(UPDATE_COMMENT_SUCCESS, response.data);
        },
        (error) => {
          commit(UPDATE_COMMENT_FAILURE, error.message);
        },
      );
  },

  deleteComment({ commit }, comment: AlertEventComment) {
    commit(DELETE_COMMENT);
    httpClient
      .delete(
        `${HOSTS.AWS}/${ENDPOINTS.EVENTS}/${comment.alertEvent.id}/${ENDPOINTS.COMMENTS}/${comment.id}`,
      )
      .then(
        () => {
          commit(DELETE_COMMENT_SUCCESS, comment);
        },
        (error) => {
          commit(DELETE_COMMENT_FAILURE, error.message);
        },
      );
  },

  clearAlertsStore({ commit }) {
    commit(CLEAR_ALERTS_STORE);
  },

  clearEventsStore({ commit }) {
    commit(CLEAR_EVENTS_ALERT_STORE);
  },

  // preview variable
  loadVariableDatas({ commit }, params: any) {
    commit(LOAD_VARIABLE_DATA);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`, {
        params: {
          id: params.variableId,
          aggregationId: params.aggregationId,
          preferredSampling: params.sampling,
          preferredSamplingUnit: params.samplingUnit,
          start: params.periode[0],
          end: params.periode[1],
          limit: 10000,
          type: "variable",
        },
      })
      .then(
        (response: AxiosResponse<GraphData>) => {
          commit(LOAD_VARIABLE_DATA_SUCCESS, {
            ...params.variable,
            data: response.data.data,
            unit: response.data.unit.name,
            preferredSampling: response.data.sampling,
            preferredSamplingUnit: response.data.samplingUnit,
            start: response.data.start,
            end: response.data.end,
          });
        },
        (error) => {
          commit(LOAD_VARIABLE_DATA_FAILURE, error.message);
        },
      );
  },

  initDiagnosticData({ commit }) {
    commit(INIT_DIAGNOSTIC_DATA);
  },
  addDiagnosticData({ commit }, params: any) {
    commit(ADD_DIAGNOSTIC_DATA);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`, {
        params: {
          id: params.variable.object.id,
          aggregationId: params.variable.aggregation.id,
          preferredSampling: params.variable.sampling,
          preferredSamplingUnit: params.variable.samplingUnit,
          start: params.periode[0],
          end: params.periode[1],
          limit: 10000,
          timeserie: true,
          type: params.variable.type,
        },
      })
      .then(
        (response: AxiosResponse<GraphData>) => {
          commit(ADD_DIAGNOSTIC_DATA_SUCCESS, {
            ...params.variable,
            data: response.data.data,
            unit: response.data.unit.name,
          });
        },
        (error) => {
          commit(ADD_DIAGNOSTIC_DATA_FAILURE, error.message);
        },
      );
  },
};
